import type { SVGProps } from 'react'

/**
 * Lucide-compatible icon props interface
 * Matches the exact props structure used by Lucide React icons
 * This ensures seamless integration with existing Lucide icons
 */
export interface LucideIconProps extends Omit<SVGProps<SVGSVGElement>, 'size' | 'color'> {
    /** Icon size in pixels */
    size?: number | string
    /** Icon color - defaults to currentColor */
    color?: string
    /** Stroke width - defaults to 2 */
    strokeWidth?: number | string
    /** Whether to use absolute stroke width */
    absoluteStrokeWidth?: boolean
}

/**
 * Base interface for all custom icon components
 * Extends Lucide's props for consistency
 */
export interface IconProps extends LucideIconProps {
    /** Additional CSS classes */
    className?: string
}

/**
 * Props for icons that support different variants or styles
 */
export interface VariantIconProps extends IconProps {
    /** Icon variant or style */
    variant?: 'default' | 'outline' | 'filled' | 'minimal'
}

/**
 * Props for brand or logo icons that might need different sizes
 */
export interface LogoIconProps extends IconProps {
    /** Logo variant */
    variant?: 'full' | 'symbol' | 'wordmark'
}

/**
 * Common sizes used throughout the application
 * Provides consistent sizing across all icons
 */
export const IconSizes = {
    xs: 12,
    sm: 16,
    base: 20,
    lg: 24,
    xl: 32,
    '2xl': 40,
    '3xl': 48,
} as const

export type IconSize = keyof typeof IconSizes
